import type { Permission, SessionView } from "@dentos/contracts";
import {
  Building2,
  ClipboardPlus,
  CalendarDays,
  ListOrdered,
  Hourglass,
  ClipboardList,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  ListChecks,
  FlaskConical,
  BarChart3,
  ShieldPlus,
  Megaphone,
  Target,
  UserPlus,
  Package,
  Zap,
  Receipt,
  Settings,
  ShieldCheck,
  Stethoscope,
  Tags,
  UserCog,
  Users,
  X,
  type LucideIcon, Plug, Database, Droplets, Pill } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { Select, cx } from "../components/ui";
import { api } from "../lib/api";
import { BrandMark } from "../lib/branding";
import { useSession } from "../lib/session";
import { TrialBanner } from "../routes/plan-usage";
import { useIsDental } from "../lib/specialty";
import { InstallAppButton } from "../components/install-app";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Shown in the phone bottom bar. */
  primary?: boolean;
  /** Dental edition only (e.g. dental lab work). */
  dentalOnly?: boolean;
  /** Ayurveda edition only (e.g. therapies). */
  ayurvedaOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: Home, primary: true },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, permission: "appointments.view", primary: true },
  { to: "/queue", label: "Queue", icon: ListOrdered, permission: "appointments.view", primary: true },
  { to: "/patients", label: "Patients", icon: Users, permission: "patients.view", primary: true },
  { to: "/therapies", label: "Therapies", icon: Droplets, permission: "therapy.view", ayurvedaOnly: true },
  { to: "/pharmacy", label: "Pharmacy", icon: Pill, permission: "pharmacy.dispense", ayurvedaOnly: true },
  { to: "/messages", label: "Messages", icon: MessageCircle, permission: "communications.view" },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/billing", label: "Billing", icon: Receipt, permission: "billing.view" },
  { to: "/insurance", label: "Insurance", icon: ShieldPlus, permission: "billing.view" },
  { to: "/reports", label: "Reports", icon: BarChart3, permission: "reports.view" },
  { to: "/leads", label: "Leads", icon: UserPlus, permission: "crm.manage" },
  { to: "/treatment-follow-up", label: "Treatment follow-up", icon: Target, permission: "crm.manage" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, permission: "campaigns.manage" },
  { to: "/labs", label: "Lab work", icon: FlaskConical, permission: "labs.manage", dentalOnly: true },
  { to: "/inventory", label: "Inventory", icon: Package, permission: "inventory.view" },
  { to: "/waitlist", label: "Waitlist", icon: Hourglass, permission: "appointments.view" },
  { to: "/doctors", label: "Doctors", icon: Stethoscope, permission: "doctors.view" },
  { to: "/services", label: "Services", icon: Tags, permission: "services.view" },
  { to: "/clinical-setup", label: "Clinical setup", icon: ClipboardPlus, permission: "clinical.view" },
  { to: "/staff", label: "Staff", icon: UserCog, permission: "staff.view" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, permission: "staff.view" },
  { to: "/branches", label: "Branches & rooms", icon: Building2, permission: "branches.view" },
  { to: "/automation", label: "Messaging", icon: Zap, permission: "communications.view" },
  { to: "/settings", label: "Settings", icon: Settings, permission: "settings.view" },
  { to: "/data", label: "Data", icon: Database, permission: "data.export" },
  { to: "/records", label: "Records & domains", icon: ShieldCheck, permission: "settings.manage" },
  { to: "/integrations", label: "Integrations", icon: Plug, permission: "settings.manage" },
  { to: "/audit", label: "Audit log", icon: ClipboardList, permission: "audit.view" },
];

function ClinicSwitcher({ session }: { session: SessionView }) {
  const { setSession } = useSession();
  const navigate = useNavigate();
  if (session.tenants.length < 2) return <p className="truncate text-sm font-semibold">{session.activeTenant?.name}</p>;
  return (
    <Select
      aria-label="Clinic"
      value={session.activeTenant?.id ?? ""}
      onChange={async (e) => {
        setSession(await api.post<SessionView>("/api/auth/switch-tenant", { tenantId: e.target.value }));
        navigate("/");
      }}
      className="min-h-9 py-1 text-sm font-semibold"
    >
      {session.tenants.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </Select>
  );
}

export function AppShell({ platform = false }: { platform?: boolean }) {
  const { session, can, setSession } = useSession();
  const isDental = useIsDental();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // A new page starts at the top. Only the path counts, so switching tabs (?tab=…) keeps the position.
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  if (!session) return null;
  const items = platform ? [] : NAV.filter((n) => (!n.permission || can(n.permission)) && (!n.dentalOnly || isDental) && (!n.ayurvedaOnly || !isDental));

  const logout = async () => {
    await api.post("/api/auth/logout").catch(() => undefined);
    setSession(null);
    navigate("/login");
  };

  const link = (n: NavItem, onClick?: () => void) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        cx("flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium", isActive ? "bg-brand-50 text-brand-800" : "text-slate-700 hover:bg-slate-100")
      }
    >
      <n.icon className="size-5 shrink-0" aria-hidden />
      {n.label}
    </NavLink>
  );

  const sidebar = (onNavigate?: () => void) => (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <BrandMark />
        <div className="min-w-0 flex-1">{platform ? <p className="text-sm font-semibold">Platform admin</p> : <ClinicSwitcher session={session} />}</div>
      </div>
      <nav aria-label="Main" className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {items.map((n) => link(n, onNavigate))}
        {session.isPlatformAdmin && link({ to: "/platform", label: "Platform admin", icon: ShieldCheck }, onNavigate)}
        {platform && session.activeTenant && link({ to: "/", label: "Back to clinic", icon: Home }, onNavigate)}
      </nav>
      <div className="border-t border-slate-200 pt-3">
        <InstallAppButton className="mb-1" />
        <NavLink to={platform ? "/platform/account" : "/account"} onClick={onNavigate} className="block truncate rounded-lg px-3 py-2 text-sm hover:bg-slate-100">
          <span className="block font-medium">{session.user.fullName}</span>
          <span className="block truncate text-xs text-slate-500">{session.user.email}</span>
        </NavLink>
        <button type="button" onClick={logout} className="mt-1 flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm text-slate-700 hover:bg-slate-100">
          <LogOut className="size-5" aria-hidden /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-white focus:p-2">
        Skip to content
      </a>
      <aside className="hidden border-r border-slate-200 bg-white lg:block lg:sticky lg:top-0 lg:h-dvh">{sidebar()}</aside>

      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-2 backdrop-blur lg:hidden">
        <button type="button" onClick={() => setOpen(true)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="Open menu">
          <Menu className="size-6" />
        </button>
        <p className="truncate font-semibold">{platform ? "Platform admin" : session.activeTenant?.name}</p>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Close menu" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl">
            <button type="button" onClick={() => setOpen(false)} className="absolute right-2 top-2 rounded-lg p-2 hover:bg-slate-100" aria-label="Close menu">
              <X className="size-5" />
            </button>
            {sidebar(() => setOpen(false))}
          </div>
        </div>
      )}

      <main id="main" className="mx-auto w-full max-w-6xl px-4 pb-24 pt-4 sm:px-6 lg:pb-10 lg:pt-8">
        {!platform && <TrialBanner />}
        <Outlet />
      </main>

      {!platform && (
        <nav aria-label="Quick" className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden">
          {items
            .filter((n) => n.primary)
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => cx("flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs", isActive ? "text-brand-700" : "text-slate-500")}
              >
                <n.icon className="size-5" aria-hidden />
                {n.label}
              </NavLink>
            ))}
        </nav>
      )}
    </div>
  );
}
